import { createScopedLogger } from '~/utils/logger';
import { apiGetLockedItems, apiUpdateLockedItems } from '~/lib/api/chats';

const logger = createScopedLogger('LockedFiles');

export interface LockedItem {
  chatId: string; // Chat ID to scope locks to a specific project
  path: string;
  isFolder: boolean; // Indicates if this is a folder lock
}

// In-memory cache for locked items
let lockedItemsCache: LockedItem[] | null = null;

// Map for faster lookups by chatId and path
const lockedItemsMap = new Map<string, Map<string, LockedItem>>();

// Debounce timer for server writes
let saveDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const SAVE_DEBOUNCE_MS = 300;

// Track the current chat ID for server persistence
let currentChatId: string | null = null;

// Legacy localStorage key for one-time migration
const LEGACY_LOCKED_FILES_KEY = 'dunea.lockedFiles';

/**
 * Get a chat-specific map from the lookup maps
 */
function getChatMap(chatId: string, createIfMissing = false): Map<string, LockedItem> | undefined {
  if (createIfMissing && !lockedItemsMap.has(chatId)) {
    lockedItemsMap.set(chatId, new Map());
  }

  return lockedItemsMap.get(chatId);
}

/**
 * Initialize the in-memory cache from the server for a specific chat.
 * Must be called with a chatId before locked items operations.
 */
export async function initializeLockedItemsFromServer(chatId: string): Promise<LockedItem[]> {
  currentChatId = chatId;

  try {
    const items = await apiGetLockedItems(chatId);

    // Normalize items
    const normalizedItems: LockedItem[] = (items || []).map((item: any) => ({
      chatId: item.chatId || chatId,
      path: item.path,
      isFolder: item.isFolder !== undefined ? item.isFolder : false,
    }));

    lockedItemsCache = normalizedItems;
    rebuildLookupMaps(normalizedItems);

    return normalizedItems;
  } catch (error) {
    logger.error('Failed to load locked items from server', error);

    // Check for legacy localStorage data to migrate
    if (typeof localStorage !== 'undefined') {
      const legacyData = localStorage.getItem(LEGACY_LOCKED_FILES_KEY);

      if (legacyData) {
        try {
          const legacyItems: LockedItem[] = JSON.parse(legacyData).map((item: any) => ({
            chatId: item.chatId || chatId,
            path: item.path,
            isFolder: item.isFolder ?? false,
          }));

          const chatItems = legacyItems.filter((i) => i.chatId === chatId);

          if (chatItems.length > 0) {
            lockedItemsCache = chatItems;
            rebuildLookupMaps(chatItems);

            // Push to server and clear localStorage
            apiUpdateLockedItems(chatId, chatItems)
              .then(() => localStorage.removeItem(LEGACY_LOCKED_FILES_KEY))
              .catch(console.error);

            return chatItems;
          }
        } catch {
          // Ignore parse errors
        }
      }
    }

    lockedItemsCache = [];

    return [];
  }
}

/**
 * Rebuild the lookup maps from the items array
 */
function rebuildLookupMaps(items: LockedItem[]): void {
  lockedItemsMap.clear();

  for (const item of items) {
    if (!lockedItemsMap.has(item.chatId)) {
      lockedItemsMap.set(item.chatId, new Map());
    }

    const chatMap = lockedItemsMap.get(item.chatId)!;
    chatMap.set(item.path, item);
  }
}

/**
 * Save locked items to server with debouncing
 */
export function saveLockedItems(items: LockedItem[]): void {
  // Update the in-memory cache immediately
  lockedItemsCache = [...items];

  // Rebuild the lookup maps
  rebuildLookupMaps(items);

  // Debounce the server write
  if (saveDebounceTimer) {
    clearTimeout(saveDebounceTimer);
  }

  saveDebounceTimer = setTimeout(() => {
    if (!currentChatId) {
      return;
    }

    const chatItems = items.filter((i) => i.chatId === currentChatId);
    apiUpdateLockedItems(currentChatId, chatItems).catch((err) =>
      logger.error('Failed to save locked items to server', err),
    );
  }, SAVE_DEBOUNCE_MS);
}

/**
 * Get locked items from cache
 */
export function getLockedItems(): LockedItem[] {
  if (lockedItemsCache !== null) {
    return lockedItemsCache;
  }

  // Cache not yet initialized — return empty. Caller should have called initializeLockedItemsFromServer first.
  lockedItemsCache = [];

  return [];
}

/**
 * Add a file or folder to the locked items list
 */
export function addLockedItem(chatId: string, path: string, isFolder: boolean = false): void {
  const lockedItems = getLockedItems();
  const newItem = { chatId, path, isFolder };

  const chatMap = getChatMap(chatId, true)!;
  chatMap.set(path, newItem);

  const filteredItems = lockedItems.filter((item) => !(item.chatId === chatId && item.path === path));
  filteredItems.push(newItem);

  saveLockedItems(filteredItems);

  logger.info(`Added locked ${isFolder ? 'folder' : 'file'}: ${path} for chat: ${chatId}`);
}

/**
 * Add a file to the locked items list (for backward compatibility)
 */
export function addLockedFile(chatId: string, filePath: string): void {
  addLockedItem(chatId, filePath);
}

/**
 * Add a folder to the locked items list
 */
export function addLockedFolder(chatId: string, folderPath: string): void {
  addLockedItem(chatId, folderPath);
}

/**
 * Remove an item from the locked items list
 */
export function removeLockedItem(chatId: string, path: string): void {
  const lockedItems = getLockedItems();
  const chatMap = getChatMap(chatId);

  if (chatMap) {
    chatMap.delete(path);
  }

  const filteredItems = lockedItems.filter((item) => !(item.chatId === chatId && item.path === path));
  saveLockedItems(filteredItems);

  logger.info(`Removed lock for: ${path} in chat: ${chatId}`);
}

/**
 * Remove a file from the locked items list (for backward compatibility)
 */
export function removeLockedFile(chatId: string, filePath: string): void {
  removeLockedItem(chatId, filePath);
}

/**
 * Remove a folder from the locked items list
 */
export function removeLockedFolder(chatId: string, folderPath: string): void {
  removeLockedItem(chatId, folderPath);
}

/**
 * Check if a path is directly locked (not considering parent folders)
 */
export function isPathDirectlyLocked(chatId: string, path: string): { locked: boolean; isFolder?: boolean } {
  getLockedItems();

  const chatMap = getChatMap(chatId);

  if (chatMap) {
    const lockedItem = chatMap.get(path);

    if (lockedItem) {
      return { locked: true, isFolder: lockedItem.isFolder };
    }
  }

  return { locked: false };
}

/**
 * Check if a file is locked, either directly or by a parent folder
 */
export function isFileLocked(chatId: string, filePath: string): { locked: boolean; lockedBy?: string } {
  getLockedItems();

  const chatMap = getChatMap(chatId);

  if (chatMap) {
    const directLock = chatMap.get(filePath);

    if (directLock && !directLock.isFolder) {
      return { locked: true, lockedBy: filePath };
    }
  }

  return checkParentFolderLocks(chatId, filePath);
}

/**
 * Check if a folder is locked
 */
export function isFolderLocked(chatId: string, folderPath: string): { locked: boolean; lockedBy?: string } {
  getLockedItems();

  const chatMap = getChatMap(chatId);

  if (chatMap) {
    const directLock = chatMap.get(folderPath);

    if (directLock && directLock.isFolder) {
      return { locked: true, lockedBy: folderPath };
    }
  }

  return checkParentFolderLocks(chatId, folderPath);
}

/**
 * Helper function to check if any parent folder of a path is locked
 */
function checkParentFolderLocks(chatId: string, path: string): { locked: boolean; lockedBy?: string } {
  const chatMap = getChatMap(chatId);

  if (!chatMap) {
    return { locked: false };
  }

  const pathParts = path.split('/');
  let currentPath = '';

  for (let i = 0; i < pathParts.length - 1; i++) {
    currentPath = currentPath ? `${currentPath}/${pathParts[i]}` : pathParts[i];

    const folderLock = chatMap.get(currentPath);

    if (folderLock && folderLock.isFolder) {
      return { locked: true, lockedBy: currentPath };
    }
  }

  return { locked: false };
}

/**
 * Get all locked items for a specific chat
 */
export function getLockedItemsForChat(chatId: string): LockedItem[] {
  const allItems = getLockedItems();
  const chatMap = getChatMap(chatId);

  if (chatMap) {
    return Array.from(chatMap.values());
  }

  return allItems.filter((item) => item.chatId === chatId);
}

/**
 * Get all locked files for a specific chat (for backward compatibility)
 */
export function getLockedFilesForChat(chatId: string): LockedItem[] {
  return getLockedItemsForChat(chatId).filter((item) => !item.isFolder);
}

/**
 * Get all locked folders for a specific chat
 */
export function getLockedFoldersForChat(chatId: string): LockedItem[] {
  return getLockedItemsForChat(chatId).filter((item) => item.isFolder);
}

/**
 * Check if a path is within a locked folder
 */
export function isPathInLockedFolder(chatId: string, path: string): { locked: boolean; lockedBy?: string } {
  return checkParentFolderLocks(chatId, path);
}

/**
 * Migrate legacy locks (without chatId or isFolder) to the new format
 */
export function migrateLegacyLocks(newChatId: string): void {
  try {
    clearCache();

    if (typeof localStorage !== 'undefined') {
      const lockedItemsJson = localStorage.getItem(LEGACY_LOCKED_FILES_KEY);

      if (lockedItemsJson) {
        const lockedItems = JSON.parse(lockedItemsJson);

        if (Array.isArray(lockedItems)) {
          let hasLegacyItems = false;

          const updatedItems = lockedItems.map((item) => {
            const needsUpdate = !item.chatId || item.isFolder === undefined;

            if (needsUpdate) {
              hasLegacyItems = true;
              return {
                ...item,
                chatId: item.chatId || newChatId,
                isFolder: item.isFolder !== undefined ? item.isFolder : false,
              };
            }

            return item;
          });

          if (hasLegacyItems) {
            saveLockedItems(updatedItems);
            localStorage.removeItem(LEGACY_LOCKED_FILES_KEY);
            logger.info(`Migrated ${updatedItems.length} legacy locks to chat ID: ${newChatId}`);
          }
        }
      }
    }
  } catch (error) {
    logger.error('Failed to migrate legacy locks', error);
  }
}

/**
 * Clear the in-memory cache
 */
export function clearCache(): void {
  lockedItemsCache = null;
  lockedItemsMap.clear();
  logger.info('Cleared locked items cache');
}

/**
 * Batch operation to lock multiple items at once
 */
export function batchLockItems(chatId: string, items: Array<{ path: string; isFolder: boolean }>): void {
  if (items.length === 0) {
    return;
  }

  const lockedItems = getLockedItems();
  const pathsToLock = new Set(items.map((item) => item.path));
  const filteredItems = lockedItems.filter((item) => !(item.chatId === chatId && pathsToLock.has(item.path)));

  const newItems = items.map((item) => ({
    chatId,
    path: item.path,
    isFolder: item.isFolder,
  }));

  const updatedItems = [...filteredItems, ...newItems];
  saveLockedItems(updatedItems);

  logger.info(`Batch locked ${items.length} items for chat: ${chatId}`);
}

/**
 * Batch operation to unlock multiple items at once
 */
export function batchUnlockItems(chatId: string, paths: string[]): void {
  if (paths.length === 0) {
    return;
  }

  const lockedItems = getLockedItems();
  const pathsToUnlock = new Set(paths);

  const chatMap = getChatMap(chatId);

  if (chatMap) {
    paths.forEach((path) => chatMap.delete(path));
  }

  const filteredItems = lockedItems.filter((item) => !(item.chatId === chatId && pathsToUnlock.has(item.path)));
  saveLockedItems(filteredItems);

  logger.info(`Batch unlocked ${paths.length} items for chat: ${chatId}`);
}
